import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import {
  getPublicDevices,
  PublicCatalogQueryError,
  type PublicCatalogPage,
} from '@/features/operations/api';
import {
  appliedFilters,
  catalogPageSizes,
  catalogSortOptions,
  clearCatalogFilters,
} from '@/features/operations/catalogQuery';
import { AppliedFilterChips } from '@/features/operations/components/AppliedFilterChips';
import { CatalogDeviceCard } from '@/features/operations/components/CatalogDeviceCard';
import { CatalogFilterPanel } from '@/features/operations/components/CatalogFilterPanel';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';

type LoadError = 'query' | 'network';

/**
 * The public catalogue (CAT-1, CAT-4). On a desktop the filters sit in a left column; on a phone
 * they open full screen from a sticky bar, as Amazon and Mercado Libre do. The URL is the source
 * of truth, so reloading or sharing it shows the same result.
 */
export function CatalogPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { formatNullablePrice } = usePriceDisplay();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const [data, setData] = useState<PublicCatalogPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const listTop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getPublicDevices(new URLSearchParams(searchKey))
      .then((page) => {
        if (active) setData(page);
      })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof PublicCatalogQueryError ? 'query' : 'network');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [searchKey, requestVersion]);

  const options = data?.filterOptions ?? null;
  const applied = appliedFilters(searchParams, options);
  const devices = data?.devices ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? 12;
  const page = data?.page ?? 1;
  const pageCount = Math.ceil(total / pageSize);
  const hasCatalogue = Boolean(data && (total > 0 || applied.length > 0));
  const sort = searchParams.get('sort') ?? 'recent';

  const scrollToList = () =>
    listTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  /** Sort and page size change what is listed first, so they start again from the first page. */
  const setOrdering = (name: 'sort' | 'pageSize', value: string, fallback: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === fallback) next.delete(name);
    else next.set(name, value);
    next.delete('page');
    setSearchParams(next);
  };

  const goToPage = (target: number) => {
    const next = new URLSearchParams(searchParams);
    if (target > 1) next.set('page', String(target));
    else next.delete('page');
    setSearchParams(next);
    scrollToList();
  };

  const clearFilters = () => setSearchParams(clearCatalogFilters(searchParams));

  const resultLabel = loading
    ? 'Buscando equipos...'
    : total === 1
      ? '1 equipo'
      : devices.length && pageCount > 1
        ? `Mostrando ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} de ${total} equipos`
        : `${total} equipos`;

  const filterPanel = (
    <CatalogFilterPanel params={searchParams} options={options} onParamsChange={setSearchParams} />
  );

  const results = (
    <>
      {error === 'query' ? (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setSearchParams(new URLSearchParams())}
            >
              Limpiar filtros
            </Button>
          }
        >
          Algunos filtros del enlace no son válidos. Limpialos para ver el catálogo.
        </Alert>
      ) : null}
      {error === 'network' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setRequestVersion((v) => v + 1)}>
              Reintentar
            </Button>
          }
        >
          No pudimos cargar el catálogo. Intentá de nuevo.
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress color="secondary" aria-label="Cargando equipos" />
        </Box>
      ) : null}
      {!loading && !error && total === 0 && applied.length === 0 ? (
        <Alert severity="info">
          No hay equipos publicados por ahora. Volvé a revisar más adelante.
        </Alert>
      ) : null}
      {!loading && !error && total === 0 && applied.length > 0 ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          }
        >
          No hay equipos con esos filtros. Probá con otros o limpialos.
        </Alert>
      ) : null}
      {!loading && !error && total > 0 && devices.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => goToPage(1)}>
              Ir a la primera página
            </Button>
          }
        >
          Esta página no tiene equipos.
        </Alert>
      ) : null}
      {!loading && !error && devices.length > 0 ? (
        <>
          {isDesktop ? (
            <Grid container spacing={2}>
              {devices.map((device) => (
                <Grid item xs={12} sm={6} lg={4} key={device.id}>
                  <CatalogDeviceCard
                    device={device}
                    price={formatNullablePrice(device.salePriceMinor)}
                    variant="grid"
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack spacing={1.5}>
              {devices.map((device) => (
                <CatalogDeviceCard
                  key={device.id}
                  device={device}
                  price={formatNullablePrice(device.salePriceMinor)}
                  variant="list"
                />
              ))}
            </Stack>
          )}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {pageCount > 1 ? (
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_event, value) => goToPage(value)}
                color="primary"
                siblingCount={0}
                getItemAriaLabel={(type, item, selected) =>
                  type === 'page'
                    ? selected
                      ? `Página ${item}, actual`
                      : `Ir a la página ${item}`
                    : type === 'next'
                      ? 'Ir a la página siguiente'
                      : type === 'previous'
                        ? 'Ir a la página anterior'
                        : type === 'first'
                          ? 'Ir a la primera página'
                          : 'Ir a la última página'
                }
              />
            ) : (
              <Box />
            )}
            <TextField
              select
              size="small"
              label="Equipos por página"
              value={String(pageSize)}
              onChange={(event) => setOrdering('pageSize', event.target.value, '12')}
              sx={{ minWidth: 170 }}
            >
              {catalogPageSizes.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </>
      ) : null}
    </>
  );

  return (
    <Stack spacing={3}>
      {isDesktop ? (
        <PageHeader
          eyebrow="CATÁLOGO"
          title="Equipos disponibles"
          description="Usados revisados y certificados por PhoneTec. Solo se muestran equipos publicados y datos aprobados para compradores."
        />
      ) : (
        // On a phone the results start right away, as in Amazon and Mercado Libre.
        <Typography variant="h4" component="h1">
          Equipos disponibles
        </Typography>
      )}

      {!isDesktop && hasCatalogue ? (
        // Sticky right under the app bar, so filtering and sorting stay one tap away.
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'sticky',
            top: 64,
            zIndex: theme.zIndex.appBar - 1,
            mx: -2,
            px: 2,
            py: 1,
            bgcolor: 'background.default',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            startIcon={
              <Badge badgeContent={applied.length} color="primary">
                <TuneOutlinedIcon />
              </Badge>
            }
          >
            Filtrar
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={(event) => setSortAnchor(event.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(sortAnchor)}
            startIcon={<SwapVertIcon />}
          >
            Ordenar
          </Button>
          <Menu
            anchorEl={sortAnchor}
            open={Boolean(sortAnchor)}
            onClose={() => setSortAnchor(null)}
          >
            {catalogSortOptions.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === sort}
                onClick={() => {
                  setSortAnchor(null);
                  setOrdering('sort', option.value, 'recent');
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      ) : null}

      <Grid container spacing={3}>
        {isDesktop && hasCatalogue ? (
          <Grid item md={3}>
            <Box
              sx={{
                position: 'sticky',
                top: 96,
                maxHeight: 'calc(100vh - 112px)',
                overflowY: 'auto',
                pr: 1,
              }}
            >
              {filterPanel}
            </Box>
          </Grid>
        ) : null}
        <Grid item xs={12} md={isDesktop && hasCatalogue ? 9 : 12}>
          <Stack spacing={2}>
            <Box ref={listTop} sx={{ scrollMarginTop: isDesktop ? 96 : 128 }} />
            {hasCatalogue ? (
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography variant="body2" color="text.secondary" role="status">
                  {resultLabel}
                </Typography>
                {isDesktop ? (
                  <TextField
                    select
                    size="small"
                    label="Ordenar por"
                    value={sort}
                    onChange={(event) => setOrdering('sort', event.target.value, 'recent')}
                    sx={{ minWidth: 200 }}
                  >
                    {catalogSortOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
              </Stack>
            ) : null}
            <AppliedFilterChips
              filters={applied}
              onRemove={(filter) => setSearchParams(filter.remove(searchParams))}
              onClear={clearFilters}
            />
            {results}
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        fullScreen
        open={filtersOpen && !isDesktop}
        onClose={() => setFiltersOpen(false)}
        aria-labelledby="catalog-filters-title"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography id="catalog-filters-title" variant="h6" component="h2">
            Filtros
          </Typography>
          <IconButton aria-label="Cerrar filtros" onClick={() => setFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent>{filterPanel}</DialogContent>
        {/* Changes apply as they are made; the footer says what they leave and closes the panel. */}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
        >
          <Button
            color="inherit"
            onClick={clearFilters}
            disabled={!applied.length}
            sx={{ flexShrink: 0 }}
          >
            Limpiar
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={loading || Boolean(error)}
            onClick={() => {
              setFiltersOpen(false);
              scrollToList();
            }}
          >
            {loading
              ? 'Buscando equipos...'
              : total === 1
                ? 'Ver 1 equipo'
                : `Ver ${total} equipos`}
          </Button>
        </Stack>
      </Dialog>
    </Stack>
  );
}
